import { Lock } from 'lucide-react';
import type { ViewPrimitive, BladeContext } from '../registry';
import {
  BladeLoading,
  BladeError,
  BladeDenied,
  BladeEmpty,
  forcedBladeState,
} from '../BladeStates';
import {
  PHI_RESTRICTED_SENTINEL,
  useBladeSearch,
  type BladeSearchHit,
} from '../BladeDataSource';

// Search view-primitive (HUB-281/HUB-369) — renders results for a query
// (`params.q`) from the search-index service via the BladeDataSource seam, and
// drills a result into its record. Rendered in an EPHEMERAL blade (see
// WorkbenchShell): the query lives in the blade `params`, and because the blade
// is ephemeral the codec excludes it from the URL — so a typed query (potential
// PHI) never lands in the address bar / history (HUB-351 F3). The query is fixed
// per blade instance; re-submitting from the topbar opens a new ephemeral blade.
//
// PHI: hit titles come from indexed content. Redaction is SERVER-side (HUB-357)
// — no client-side masking here; a title that arrives as the restricted
// sentinel renders as a Lock + "Restricted" placeholder, consistent with the
// record primitive's fields. Hit ids are identifiers (non-PHI) and safe to
// drill on.

/** Hit title for display — never render the raw sentinel as a value. */
function HitTitle({ hit }: { hit: BladeSearchHit }) {
  if (hit.title === PHI_RESTRICTED_SENTINEL) {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Lock aria-hidden className="size-3.5 shrink-0" />
        <span className="italic">Restricted</span>
      </span>
    );
  }
  return <span className="truncate text-sm font-medium text-foreground">{hit.title}</span>;
}

function SearchBody({ ctx }: { ctx: BladeContext }) {
  const q = (ctx.descriptor.params?.q ?? '').trim();
  const { status, data } = useBladeSearch(ctx.descriptor);

  if (!q) {
    return <BladeEmpty title="Search" description="Type a query to find clients, claims, and codes." />;
  }
  if (status === 'loading') return <BladeLoading />;
  if (status === 'denied') return <BladeDenied />;
  if (status === 'error') return <BladeError message="Search failed. Try again." />;

  const hits = data?.hits ?? [];
  if (hits.length === 0) {
    return <BladeEmpty title="No results" description={`Nothing matches “${q}”.`} />;
  }
  return (
    <ul role="list" className="flex flex-col">
      {hits.map((h) => (
        <li key={`${h.type}:${h.id}`}>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2.5 text-left transition-colors hover:bg-muted"
            onClick={(e) => {
              e.stopPropagation();
              ctx.openChild({
                key: 'record',
                // Never propagate the sentinel as a blade title (it could reach
                // breadcrumbs); the record blade re-resolves its own title.
                title: h.title === PHI_RESTRICTED_SENTINEL ? h.id : h.title,
                eyebrow: h.type,
                entityId: h.id,
                width: 'med',
              });
            }}
          >
            <HitTitle hit={h} />
            <span className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
              {h.type}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export const searchPrimitive: ViewPrimitive = {
  key: 'search',
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === 'loading') return <BladeLoading />;
    if (forced === 'error') return <BladeError />;
    if (forced === 'denied') return <BladeDenied />;
    return <SearchBody ctx={ctx} />;
  },
};
